import { Container, Graphics, Text, TextStyle, Sprite } from 'pixi.js';
import { Assets } from 'pixi.js';
import gsap from 'gsap';
import { HealthBar } from './HealthBar';
import { COLORS, FONTS } from '../constants/theme';
import type { WarriorInstance, AvailableActions } from '@/types';

export interface WarriorCardSpriteOptions {
  warrior: WarriorInstance;
  width: number;
  height: number;
  isSelected: boolean;
  isCurrentTurn: boolean;
  isRotated?: boolean; // For portrait mode Player 2
  availableActions?: AvailableActions;
  onSelect: (instanceId: string) => void;
  onAttack?: () => void;
  onHeal?: () => void;
}

/**
 * Draw a sword icon for attack button
 */
function drawSwordIcon(graphics: Graphics, size: number, color: number): void {
  const half = size / 2;
  // Blade (triangle)
  graphics.moveTo(0, -half);
  graphics.lineTo(half * 0.3, half * 0.4);
  graphics.lineTo(-half * 0.3, half * 0.4);
  graphics.closePath();
  graphics.fill({ color });
  // Handle (rectangle)
  graphics.rect(-half * 0.15, half * 0.4, half * 0.3, half * 0.4);
  graphics.fill({ color });
  // Crossguard
  graphics.rect(-half * 0.4, half * 0.3, half * 0.8, half * 0.15);
  graphics.fill({ color });
}

/**
 * Draw a heart icon for heal button
 */
function drawHeartIcon(graphics: Graphics, size: number, color: number): void {
  const half = size / 2;
  // Two circles on top
  graphics.circle(-half * 0.3, -half * 0.2, half * 0.4);
  graphics.fill({ color });
  graphics.circle(half * 0.3, -half * 0.2, half * 0.4);
  graphics.fill({ color });
  // Triangle at bottom
  graphics.moveTo(-half * 0.65, -half * 0.1);
  graphics.lineTo(0, half * 0.7);
  graphics.lineTo(half * 0.65, -half * 0.1);
  graphics.closePath();
  graphics.fill({ color });
}

/**
 * Charge bar component for displaying cooldown progress
 */
class ChargeBar extends Container {
  private bg: Graphics;
  private fill: Graphics;
  private text: Text;
  private barWidth: number;
  private barHeight: number;

  constructor(width: number, height: number, current: number, max: number, isReady: boolean) {
    super();
    this.barWidth = width;
    this.barHeight = height;

    // Background
    this.bg = new Graphics();
    this.bg.roundRect(0, 0, width, height, 3);
    this.bg.fill({ color: COLORS.BG_TERTIARY });
    this.addChild(this.bg);

    // Fill
    this.fill = new Graphics();
    this.addChild(this.fill);

    // Text (centered)
    const fontSize = Math.max(8, Math.min(10, height * 0.8));
    this.text = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: FONTS.PRIMARY,
        fontSize,
        fontWeight: 'bold',
        fill: COLORS.TEXT_PRIMARY,
      }),
    });
    this.text.anchor.set(0.5);
    this.text.x = width / 2;
    this.text.y = height / 2;
    this.addChild(this.text);

    this.update(current, max, isReady);
  }

  update(current: number, max: number, isReady: boolean): void {
    const percentage = max > 0 ? Math.min(current / max, 1) : 1;
    const fillWidth = this.barWidth * percentage;

    // Update fill
    this.fill.clear();
    if (fillWidth > 0) {
      this.fill.roundRect(0, 0, fillWidth, this.barHeight, 3);
      this.fill.fill({ color: isReady ? COLORS.SUCCESS : COLORS.WARNING });
    }

    // Update text
    this.text.text = isReady ? 'READY' : `${current}/${max}`;
  }
}

/**
 * Warrior card displayed in the HUD
 * Layout: Sprite image (square), name, HP bar, attack row, heal row (if applicable), dash row (if applicable)
 */
export class WarriorCardSprite extends Container {
  private background: Graphics;
  private spriteImage: Sprite | null = null;
  private spriteMask: Graphics;
  private spritePlaceholder: Graphics;
  private nameText: Text;
  private healthBar: HealthBar | null = null;
  private hpLabel: Text | null = null;
  private attackChargeBar: ChargeBar | null = null;
  private healChargeBar: ChargeBar | null = null;
  private dashChargeBar: ChargeBar | null = null;
  private attackButton: Container | null = null;
  private healButton: Container | null = null;
  private options: WarriorCardSpriteOptions;
  private labelWidth: number = 0;
  private valueWidth: number = 0;
  private rows: Container[] = [];

  constructor(options: WarriorCardSpriteOptions) {
    super();
    this.options = options;

    const { width, height, warrior, isRotated } = options;

    // Apply rotation for portrait mode Player 2
    if (isRotated) {
      this.rotation = Math.PI;
    }

    // Background
    this.background = new Graphics();
    this.addChild(this.background);

    // Sprite area - SQUARE aspect ratio
    const spriteSize = width - 8; // Square size based on width
    const spriteHeight = spriteSize;

    // Sprite mask for proper clipping
    this.spriteMask = new Graphics();
    this.spriteMask.roundRect(4, 4, spriteSize, spriteHeight, 4);
    this.spriteMask.fill({ color: 0xffffff });
    this.addChild(this.spriteMask);

    // Sprite placeholder
    this.spritePlaceholder = new Graphics();
    this.spritePlaceholder.roundRect(4, 4, spriteSize, spriteHeight, 4);
    this.spritePlaceholder.fill({ color: COLORS.BG_TERTIARY });
    this.addChild(this.spritePlaceholder);

    // Load sprite
    this.loadSprite(spriteSize, spriteHeight);

    // Calculate font sizes based on card height
    const nameFontSize = Math.max(10, Math.min(14, height * 0.06));
    const labelFontSize = Math.max(8, Math.min(11, height * 0.045));

    // Name
    const nameStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: nameFontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    this.nameText = new Text({ text: warrior.definition.name, style: nameStyle });
    this.nameText.anchor.set(0.5, 0);
    this.nameText.x = width / 2;
    this.nameText.y = spriteHeight + 8;
    this.addChild(this.nameText);

    // Calculate consistent label width for alignment (use widest label)
    const labelStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: labelFontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_SECONDARY,
    });
    const testLabels = ['HP', 'ATK', 'HEAL', 'DASH'];
    this.labelWidth = Math.max(...testLabels.map(l => {
      const t = new Text({ text: l, style: labelStyle });
      const w = t.width;
      t.destroy();
      return w;
    })) + 4; // Add padding

    // Calculate consistent value width for alignment (use widest value)
    const valueStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: labelFontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const testValues = [
      String(warrior.definition.stats.health),
      String(warrior.definition.stats.damage),
      String(warrior.definition.stats.healingPower),
    ];
    this.valueWidth = Math.max(...testValues.map(v => {
      const t = new Text({ text: v, style: valueStyle });
      const w = t.width;
      t.destroy();
      return w;
    })) + 4; // Add padding

    // Calculate available space for rows
    const rowsStartY = this.nameText.y + nameFontSize + 6;
    const rowsEndY = height - 6;
    const availableHeight = rowsEndY - rowsStartY;

    // Determine number of rows
    const hasHeal = warrior.definition.stats.healingPower > 0;
    const hasDash = warrior.definition.stats.dashFrequency > 0;
    const numRows = 2 + (hasHeal ? 1 : 0) + (hasDash ? 1 : 0); // HP + ATK + optional HEAL + optional DASH

    // Calculate row height and gap for even distribution
    const rowHeight = Math.min(Math.max(16, height * 0.07), availableHeight / numRows - 2);
    const totalRowsHeight = numRows * rowHeight;
    const totalGap = availableHeight - totalRowsHeight;
    const gap = totalGap / (numRows + 1); // Distribute gap evenly including top and bottom

    let currentY = rowsStartY + gap;

    // HP Row
    const hpRow = this.createHPRow(width, rowHeight, labelFontSize, currentY);
    this.addChild(hpRow);
    this.rows.push(hpRow);
    currentY += rowHeight + gap;

    // Attack Row
    const attackRow = this.createAttackRow(width, rowHeight, labelFontSize, currentY);
    this.addChild(attackRow);
    this.rows.push(attackRow);
    currentY += rowHeight + gap;

    // Heal Row (only if warrior can heal)
    if (hasHeal) {
      const healRow = this.createHealRow(width, rowHeight, labelFontSize, currentY);
      this.addChild(healRow);
      this.rows.push(healRow);
      currentY += rowHeight + gap;
    }

    // Dash Row (only if warrior can dash)
    if (hasDash) {
      const dashRow = this.createDashRow(width, rowHeight, labelFontSize, currentY);
      this.addChild(dashRow);
      this.rows.push(dashRow);
    }

    this.draw();
    this.setupInteraction();
  }

  private createHPRow(width: number, height: number, fontSize: number, y: number): Container {
    const row = new Container();
    row.y = y;

    const { warrior } = this.options;
    const padding = 6;
    const buttonSize = height; // Reserve space for button alignment with ATK/HEAL rows
    const valueX = padding + this.labelWidth + 4;
    const barX = valueX + this.valueWidth + 4;
    const barWidth = width - barX - buttonSize - padding * 2;

    // Label
    const labelStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_SECONDARY,
    });
    this.hpLabel = new Text({ text: 'HP', style: labelStyle });
    this.hpLabel.x = padding;
    this.hpLabel.y = (height - fontSize) / 2;
    row.addChild(this.hpLabel);

    // Value (max health)
    const valueStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const valueText = new Text({ text: String(warrior.definition.stats.health), style: valueStyle });
    valueText.x = valueX;
    valueText.y = (height - fontSize) / 2;
    row.addChild(valueText);

    // Health bar (shows only current health)
    this.healthBar = new HealthBar({
      width: barWidth,
      height: height,
      currentHealth: warrior.currentHealth,
      maxHealth: warrior.definition.stats.health,
      showText: true,
      showMaxHealth: false,
    });
    this.healthBar.x = barX;
    this.healthBar.y = 0;
    row.addChild(this.healthBar);

    return row;
  }

  private createAttackRow(width: number, height: number, fontSize: number, y: number): Container {
    const row = new Container();
    row.y = y;

    const { warrior } = this.options;
    const { attackFrequency, damage } = warrior.definition.stats;
    const padding = 6;
    const buttonSize = height;
    const valueX = padding + this.labelWidth + 4;
    const barX = valueX + this.valueWidth + 4;
    const barWidth = width - barX - buttonSize - padding * 2;

    // Label
    const labelStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_SECONDARY,
    });
    const labelText = new Text({ text: 'ATK', style: labelStyle });
    labelText.x = padding;
    labelText.y = (height - fontSize) / 2;
    row.addChild(labelText);

    // Value (attack damage)
    const valueStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const valueText = new Text({ text: String(damage), style: valueStyle });
    valueText.x = valueX;
    valueText.y = (height - fontSize) / 2;
    row.addChild(valueText);

    // Charge bar
    const isReady = attackFrequency === 0 || warrior.attackCooldown >= attackFrequency;
    const current = attackFrequency === 0 ? 1 : warrior.attackCooldown;
    const max = attackFrequency === 0 ? 1 : attackFrequency;
    this.attackChargeBar = new ChargeBar(barWidth, height, current, max, isReady);
    this.attackChargeBar.x = barX;
    row.addChild(this.attackChargeBar);

    // Action button
    this.attackButton = this.createIconButton(buttonSize, 'attack', isReady);
    this.attackButton.x = width - buttonSize - padding;
    this.attackButton.y = 0;
    row.addChild(this.attackButton);

    return row;
  }

  private createHealRow(width: number, height: number, fontSize: number, y: number): Container {
    const row = new Container();
    row.y = y;

    const { warrior } = this.options;
    const { healingFrequency, healingPower } = warrior.definition.stats;
    const padding = 6;
    const buttonSize = height;
    const valueX = padding + this.labelWidth + 4;
    const barX = valueX + this.valueWidth + 4;
    const barWidth = width - barX - buttonSize - padding * 2;

    // Label
    const labelStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_SECONDARY,
    });
    const labelText = new Text({ text: 'HEAL', style: labelStyle });
    labelText.x = padding;
    labelText.y = (height - fontSize) / 2;
    row.addChild(labelText);

    // Value (healing power)
    const valueStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const valueText = new Text({ text: String(healingPower), style: valueStyle });
    valueText.x = valueX;
    valueText.y = (height - fontSize) / 2;
    row.addChild(valueText);

    // Charge bar
    const isReady = healingFrequency === 0 || warrior.healCooldown >= healingFrequency;
    const current = healingFrequency === 0 ? 1 : warrior.healCooldown;
    const max = healingFrequency === 0 ? 1 : healingFrequency;
    this.healChargeBar = new ChargeBar(barWidth, height, current, max, isReady);
    this.healChargeBar.x = barX;
    row.addChild(this.healChargeBar);

    // Action button
    this.healButton = this.createIconButton(buttonSize, 'heal', isReady);
    this.healButton.x = width - buttonSize - padding;
    this.healButton.y = 0;
    row.addChild(this.healButton);

    return row;
  }

  private createDashRow(width: number, height: number, fontSize: number, y: number): Container {
    const row = new Container();
    row.y = y;

    const { warrior } = this.options;
    const { dashFrequency } = warrior.definition.stats;
    const padding = 6;
    const buttonSize = height; // Reserve space for button alignment with ATK/HEAL rows
    const valueX = padding + this.labelWidth + 4;
    const barX = valueX + this.valueWidth + 4;
    const barWidth = width - barX - buttonSize - padding * 2;

    // Label
    const labelStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize,
      fontWeight: 'bold',
      fill: COLORS.TEXT_SECONDARY,
    });
    const labelText = new Text({ text: 'DASH', style: labelStyle });
    labelText.x = padding;
    labelText.y = (height - fontSize) / 2;
    row.addChild(labelText);

    // Empty value column (dash has no power value, but keep alignment)

    // Charge bar
    const dashReady = warrior.dashCooldown >= dashFrequency;
    this.dashChargeBar = new ChargeBar(barWidth, height, warrior.dashCooldown, dashFrequency, dashReady);
    this.dashChargeBar.x = barX;
    row.addChild(this.dashChargeBar);

    return row;
  }

  private createIconButton(size: number, type: 'attack' | 'heal', isReady: boolean): Container {
    const button = new Container();

    // Button background
    const bg = new Graphics();
    bg.roundRect(0, 0, size, size, 4);
    bg.fill({ color: isReady ? (type === 'attack' ? COLORS.DANGER : COLORS.SUCCESS) : COLORS.BG_TERTIARY });
    button.addChild(bg);

    // Icon
    const icon = new Graphics();
    icon.x = size / 2;
    icon.y = size / 2;
    const iconSize = size * 0.6;
    const iconColor = isReady ? COLORS.TEXT_PRIMARY : COLORS.TEXT_MUTED;

    if (type === 'attack') {
      drawSwordIcon(icon, iconSize, iconColor);
    } else {
      drawHeartIcon(icon, iconSize, iconColor);
    }
    button.addChild(icon);

    // Store references for updating
    (button as any).bg = bg;
    (button as any).icon = icon;
    (button as any).type = type;
    (button as any).size = size;

    // Interaction
    button.eventMode = 'static';
    button.cursor = isReady ? 'pointer' : 'default';

    button.on('pointerdown', (e) => {
      e.stopPropagation();
      const { isSelected, isCurrentTurn, availableActions, onAttack, onHeal } = this.options;
      if (!isSelected || !isCurrentTurn) return;

      if (type === 'attack' && availableActions?.canAttack && onAttack) {
        onAttack();
      } else if (type === 'heal' && availableActions?.canHeal && onHeal) {
        onHeal();
      }
    });

    button.on('pointerover', () => {
      if (isReady) {
        gsap.to(button.scale, { x: 1.1, y: 1.1, duration: 0.1 });
      }
    });

    button.on('pointerout', () => {
      gsap.to(button.scale, { x: 1, y: 1, duration: 0.1 });
    });

    return button;
  }

  private async loadSprite(width: number, height: number): Promise<void> {
    const { warrior } = this.options;

    try {
      const texture = await Assets.load(warrior.definition.images.sprite);
      this.spriteImage = new Sprite({ texture });

      // Calculate scale to cover the area while maintaining aspect ratio
      const textureAspect = texture.width / texture.height;
      const areaAspect = width / height;

      if (textureAspect > areaAspect) {
        // Texture is wider - fit to height
        this.spriteImage.height = height;
        this.spriteImage.width = height * textureAspect;
      } else {
        // Texture is taller - fit to width
        this.spriteImage.width = width;
        this.spriteImage.height = width / textureAspect;
      }

      // Center the sprite
      this.spriteImage.x = 4 + (width - this.spriteImage.width) / 2;
      this.spriteImage.y = 4 + (height - this.spriteImage.height) / 2;

      // Apply mask for proper clipping
      this.spriteImage.mask = this.spriteMask;

      this.addChildAt(this.spriteImage, 2); // After mask and placeholder
      this.spritePlaceholder.visible = false;
    } catch {
      // Keep placeholder visible
    }
  }

  private draw(): void {
    const { width, height, warrior, isSelected, isCurrentTurn } = this.options;

    // Background
    this.background.clear();
    this.background.roundRect(0, 0, width, height, 6);
    this.background.fill({ color: COLORS.BG_SECONDARY });

    // Border based on selection/turn
    const borderColor = isSelected
      ? COLORS.TILE_SELECTED
      : warrior.ownerId === 'player1'
        ? COLORS.PLAYER1
        : COLORS.PLAYER2;
    const borderWidth = isSelected ? 3 : 2;

    this.background.roundRect(0, 0, width, height, 6);
    this.background.stroke({ color: borderColor, width: borderWidth });

    // Dim if not current turn
    this.alpha = isCurrentTurn ? 1 : 0.6;

    // Update button states
    this.updateButtonStates();
  }

  private updateButtonStates(): void {
    const { isSelected, isCurrentTurn, availableActions } = this.options;
    const canInteract = isSelected && isCurrentTurn;

    // Update attack button
    if (this.attackButton) {
      const canAttack = canInteract && availableActions?.canAttack;
      const btn = this.attackButton as any;
      const size = btn.size;

      // Update background
      btn.bg.clear();
      btn.bg.roundRect(0, 0, size, size, 4);
      btn.bg.fill({ color: canAttack ? COLORS.DANGER : COLORS.BG_TERTIARY });

      // Update icon
      btn.icon.clear();
      const iconSize = size * 0.6;
      const iconColor = canAttack ? COLORS.TEXT_PRIMARY : COLORS.TEXT_MUTED;
      drawSwordIcon(btn.icon, iconSize, iconColor);

      this.attackButton.cursor = canAttack ? 'pointer' : 'default';
      this.attackButton.alpha = canAttack ? 1 : 0.5;
    }

    // Update heal button
    if (this.healButton) {
      const canHeal = canInteract && availableActions?.canHeal;
      const btn = this.healButton as any;
      const size = btn.size;

      // Update background
      btn.bg.clear();
      btn.bg.roundRect(0, 0, size, size, 4);
      btn.bg.fill({ color: canHeal ? COLORS.SUCCESS : COLORS.BG_TERTIARY });

      // Update icon
      btn.icon.clear();
      const iconSize = size * 0.6;
      const iconColor = canHeal ? COLORS.TEXT_PRIMARY : COLORS.TEXT_MUTED;
      drawHeartIcon(btn.icon, iconSize, iconColor);

      this.healButton.cursor = canHeal ? 'pointer' : 'default';
      this.healButton.alpha = canHeal ? 1 : 0.5;
    }
  }

  private updateChargeBars(): void {
    const { warrior } = this.options;
    const { attackFrequency, healingFrequency, dashFrequency } = warrior.definition.stats;

    // Update attack charge bar
    if (this.attackChargeBar) {
      const isReady = attackFrequency === 0 || warrior.attackCooldown >= attackFrequency;
      const current = attackFrequency === 0 ? 1 : warrior.attackCooldown;
      const max = attackFrequency === 0 ? 1 : attackFrequency;
      this.attackChargeBar.update(current, max, isReady);
    }

    // Update heal charge bar
    if (this.healChargeBar && warrior.definition.stats.healingPower > 0) {
      const isReady = healingFrequency === 0 || warrior.healCooldown >= healingFrequency;
      const current = healingFrequency === 0 ? 1 : warrior.healCooldown;
      const max = healingFrequency === 0 ? 1 : healingFrequency;
      this.healChargeBar.update(current, max, isReady);
    }

    // Update dash charge bar
    if (this.dashChargeBar && dashFrequency > 0) {
      const dashReady = warrior.dashCooldown >= dashFrequency;
      this.dashChargeBar.update(warrior.dashCooldown, dashFrequency, dashReady);
    }
  }

  private setupInteraction(): void {
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerdown', (e) => {
      // Don't trigger card selection if clicking on buttons
      if (this.attackButton && e.target === this.attackButton) return;
      if (this.healButton && e.target === this.healButton) return;
      this.options.onSelect(this.options.warrior.instanceId);
    });

    this.on('pointerover', () => {
      if (this.options.isCurrentTurn) {
        gsap.to(this.scale, { x: 1.02, y: 1.02, duration: 0.15 });
      }
    });

    this.on('pointerout', () => {
      gsap.to(this.scale, { x: 1, y: 1, duration: 0.15 });
    });
  }

  /**
   * Update the card with new warrior state
   */
  update(
    warrior: WarriorInstance,
    isSelected: boolean,
    isCurrentTurn: boolean,
    availableActions?: AvailableActions
  ): void {
    this.options.warrior = warrior;
    this.options.isSelected = isSelected;
    this.options.isCurrentTurn = isCurrentTurn;
    this.options.availableActions = availableActions;

    // Update health bar
    this.healthBar?.setHealth(warrior.currentHealth, warrior.definition.stats.health);

    // Update charge bars
    this.updateChargeBars();

    this.draw();
  }

  /**
   * Get the warrior instance ID
   */
  getWarriorId(): string {
    return this.options.warrior.instanceId;
  }
}
